import React, { useEffect, useMemo, useState } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import PropertyCardSkeleton from '../components/PropertyCardSkeleton';
import { apiEndpoints } from '../config/api';
import { Link } from 'react-router-dom';

const propertyListingsEndpoint = apiEndpoints.propertyListings;
const pageSize = 12;
const defaultPropertyCategories = ['Residential', 'Commercial'];
const defaultUnitTypes = {
  residential: ['APARTMENT', 'DUPLEX', 'PENTHOUSE', 'VILLA', 'TOWNHOUSE', 'HOTEL APARTMENT', 'RESIDENTIAL BUILDING', 'RESIDENTIAL PLOT'],
  commercial: ['COMMERCIAL BUILDING', 'OFFICE', 'HOTEL']
};

const emptyFilters = {
  community: '',
  property_category: '',
  unit_type: '',
  developer: '',
  city: '',
  min_price: '',
  max_price: '',
  sort: ''
};

const slugify = (value, fallback) => {
  const result = String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return result || fallback;
};

const detailsUrl = (property) => {
  const developer = slugify(property.developer, 'developer');
  const propertySlug = slugify(property.seo_slug || property.title, property.reference_number || 'property');
  return `/new-projects/${developer}/${propertySlug}`;
};

const formatPrice = (value) => {
  const number = Number.parseFloat(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(number) && number > 0
    ? `AED ${Math.round(number).toLocaleString('en-US')}`
    : 'Price on request';
};

const filtersFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return Object.keys(emptyFilters).reduce((filters, key) => {
    filters[key] = params.get(key) || '';
    return filters;
  }, {});
};

const Properties = () => {
  const [filters, setFilters] = useState(filtersFromUrl);
  const [appliedFilters, setAppliedFilters] = useState(filtersFromUrl);
  const [properties, setProperties] = useState([]);
  const [options, setOptions] = useState({
    communities: [],
    communityLocations: [],
    propertyCategories: defaultPropertyCategories,
    unitTypes: defaultUnitTypes,
    developers: [],
    cities: []
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [communitySuggestionsOpen, setCommunitySuggestionsOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add('elevate-home', 'inner-page');
    document.title = 'Properties - Elevate Realty';
    return () => document.body.classList.remove('elevate-home', 'inner-page');
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${propertyListingsEndpoint}?optionsOnly=1`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Filter request failed with ${response.status}`);
        return response.json();
      })
      .then((payload) => setOptions((current) => ({ ...current, ...(payload?.data || {}) })))
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError('Property filters are temporarily unavailable.');
      });
    return () => controller.abort();
  }, []);

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (String(value).trim()) params.set(key, String(value).trim());
    });
    return params.toString();
  }, [appliedFilters, page]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    const cleanParams = new URLSearchParams();
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (String(value).trim()) cleanParams.set(key, String(value).trim());
    });
    window.history.replaceState(null, '', `/new-projects${cleanParams.toString() ? `?${cleanParams}` : ''}`);
    fetch(`${propertyListingsEndpoint}?${queryString}`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Property request failed with ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (payload?.result !== 1) throw new Error(payload?.message || 'Could not load properties.');
        const data = payload?.data || {};
        const nextProperties = Array.isArray(data.properties) ? data.properties : [];
        setProperties((current) => {
          if (page === 1) return nextProperties;
          const existingReferences = new Set(current.map((property) => property.reference_number));
          return [...current, ...nextProperties.filter((property) => !existingReferences.has(property.reference_number))];
        });
        setTotalPages(Number(data.totalPages) || 1);
        setTotalRecords(Number(data.totalRecords) || 0);
      })
      .catch((requestError) => {
        if (requestError.name === 'AbortError') return;
        if (page === 1) {
          setProperties([]);
          setTotalRecords(0);
        }
        setError('Properties are temporarily unavailable. Please try again.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [appliedFilters, page, queryString]);

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const updateSort = (event) => {
    const sort = event.target.value;
    setFilters((current) => ({ ...current, sort }));
    setAppliedFilters((current) => ({ ...current, sort }));
    setPage(1);
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters(filters);
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(1);
    setCommunitySuggestionsOpen(false);
  };

  const communityQuery = filters.community.trim().toLowerCase();
  const matchedCommunities = communityQuery
    ? options.communities
        .filter((community) => community.toLowerCase().includes(communityQuery))
        .slice(0, 7)
    : [];

  const selectCommunity = (community) => {
    const location = options.communityLocations.find(
      (item) => item.community.toLowerCase() === community.toLowerCase()
    );
    setFilters((current) => ({
      ...current,
      community,
      city: location?.city || ''
    }));
    setCommunitySuggestionsOpen(false);
  };

  const updateCityFilter = (event) => {
    const city = event.target.value;
    setFilters((current) => {
      const selectedCommunity = options.communityLocations.find(
        (item) => item.community.toLowerCase() === current.community.trim().toLowerCase()
      );
      const communityIsCompatible = !current.community || !city || !selectedCommunity?.city
        || selectedCommunity.city.toLowerCase() === city.toLowerCase();
      return {
        ...current,
        city,
        community: communityIsCompatible ? current.community : ''
      };
    });
  };

  const updatePropertyCategory = (event) => {
    const propertyCategory = event.target.value;
    setFilters((current) => {
      const allowedTypes = propertyCategory === 'Commercial'
        ? options.unitTypes.commercial
        : propertyCategory === 'Residential'
          ? options.unitTypes.residential
          : [...options.unitTypes.residential, ...options.unitTypes.commercial];
      return {
        ...current,
        property_category: propertyCategory,
        unit_type: allowedTypes.includes(current.unit_type) ? current.unit_type : ''
      };
    });
  };

  const updateUnitType = (event) => {
    const unitType = event.target.value;
    const propertyCategory = options.unitTypes.commercial.includes(unitType)
      ? 'Commercial'
      : options.unitTypes.residential.includes(unitType)
        ? 'Residential'
        : filters.property_category;
    setFilters((current) => ({
      ...current,
      unit_type: unitType,
      property_category: unitType ? propertyCategory : current.property_category
    }));
  };

  const availableUnitTypes = filters.property_category === 'Commercial'
    ? options.unitTypes.commercial
    : filters.property_category === 'Residential'
      ? options.unitTypes.residential
      : [...options.unitTypes.residential, ...options.unitTypes.commercial];

  return (
    <>
      <Header />
      <main className="inner-main" id="top">
        <section className="inner-hero properties-hero">
          <div className="inner-hero__content">
            <p className="eyebrow">Properties</p>
            <h1>Residences selected with <em>intent.</em></h1>
            <p>A curated view of off-plan launches, private villas and penthouses across Dubai's most sought-after addresses.</p>
          </div>
        </section>

        <div className="property-toolbar" aria-label="Property search">
          <form onSubmit={applyFilters}>
            <div className="property-toolbar__field property-autocomplete-field">
              <span>Location</span>
              <input
                id="property-community"
                type="text"
                name="community"
                value={filters.community}
                onChange={(event) => {
                  setFilters((current) => ({
                    ...current,
                    community: event.target.value,
                    city: ''
                  }));
                  setCommunitySuggestionsOpen(true);
                }}
                onFocus={() => setCommunitySuggestionsOpen(true)}
                onBlur={() => setCommunitySuggestionsOpen(false)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setCommunitySuggestionsOpen(false);
                }}
                placeholder="Area, development..."
                autoComplete="off"
                role="combobox"
                aria-label="Search location"
                aria-autocomplete="list"
                aria-controls="property-community-options"
                aria-expanded={communitySuggestionsOpen && matchedCommunities.length > 0}
              />
              {communitySuggestionsOpen && matchedCommunities.length > 0 && (
                <div className="property-autocomplete-menu" id="property-community-options" role="listbox">
                  {matchedCommunities.map((community) => (
                    <button
                      key={community}
                      type="button"
                      role="option"
                      aria-selected={filters.community === community}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        selectCommunity(community);
                      }}
                    >
                      {community}
                    </button>
                  ))}
                </div>
              )}
            </div> 
            <label>
              <span>Property Type</span>
              <select name="property_category" value={filters.property_category} onChange={updatePropertyCategory}>
                <option value="">Residential or Commercial</option>
                {options.propertyCategories.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
             <label>
              <span>Unit Type</span>
              <select name="unit_type" value={filters.unit_type} onChange={updateUnitType}>
                <option value="">Any unit type</option>
                {availableUnitTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
            <label>
              <span>Developer</span>
              <select name="developer" value={filters.developer} onChange={updateFilter}>
                <option value="">Any developer</option>
                {options.developers.map((developer) => <option key={developer} value={developer}>{developer}</option>)}
              </select>
            </label>
            <button type="submit">Search</button>
          </form>
          <div className="property-toolbar__more">
            <label>
              <span>City</span>
              <select name="city" value={filters.city} onChange={updateCityFilter}>
                <option value="">Any city</option>
                {options.cities.map((city) => <option key={city} value={city}>{city}</option>)}
              </select>
            </label>
            <label>
              <span>Min Price</span>
              <input type="number" min="0" name="min_price" value={filters.min_price} onChange={updateFilter} placeholder="AED minimum" />
            </label>
            <label>
              <span>Max Price</span>
              <input type="number" min="0" name="max_price" value={filters.max_price} onChange={updateFilter} placeholder="AED maximum" />
            </label>
            <button type="button" className="property-filter-reset" onClick={resetFilters}>Reset</button>
          </div>
        </div>

        <section className="inner-section" id="listings">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Current selection</p>
              <h2>Private homes and investment-grade addresses.</h2>
              {!loading && <p className="property-result-count">{totalRecords.toLocaleString('en-US')} properties found</p>}
            </div>
            <div className="section-heading__actions">
              <Link to="/contact-us">Request private list</Link>
              <label className="property-sort">
                <span>Sort</span>
                <select name="sort" value={filters.sort} onChange={updateSort} aria-label="Sort properties">
                  <option value="">Default</option>
                  <option value="featured">Featured</option>
                  <option value="newest">Newest</option>
                  <option value="price_low">Price (Low)</option>
                  <option value="price_high">Price (High)</option>
                </select>
              </label>
            </div>
          </div>

          {error && <div className="property-status property-status--error" role="alert">{error}</div>}
          {loading && properties.length === 0 && (
            <div className="property-grid properties-page-grid property-skeleton-grid" aria-label="Loading properties">
              {[1, 2, 3].map((item) => <PropertyCardSkeleton key={item} />)}
            </div>
          )}
          {!loading && !error && properties.length === 0 && (
            <div className="property-status">No properties match these filters. Try a different location or price range.</div>
          )}

          {properties.length > 0 && (
            <div className="property-grid properties-page-grid">
              {properties.map((property) => {
                const url = detailsUrl(property);
                const title = property.title || 'Dubai property';
                return (
                  <article className="property-card" key={property.reference_number}>
                    <Link className="property-image" to={url} aria-label={`View details for ${title}`} data-hover-label="View property">
                      <img src={property.ImagePath || '/assets/img/property-img-1.jpg'} alt={title} loading="lazy" />
                      <span>{property.property_type || property.property_type || 'Property'}</span>
                    </Link>
                    <div className="property-content">
                      <div className="property-meta">
                        <span>{property.community},</span>
                        <span>{property.city || 'Dubai'}</span>
                      </div>
                      <h3>{title}</h3>
                      <p>{property.short_description || `Explore this ${property.property_type || 'property'} by ${property.developer || 'a leading developer'} in ${property.community || property.city || 'Dubai'}.`}</p>
                      <div className="property-specs">
                      
                        {property.size && <span><i class="fa-solid fa-maximize" style={{paddingRight:5}}></i> <span>{property.size}</span> sq. ft.</span>}
                        {property.developer && <span>{property.developer}</span>}
                      </div>
                      <div className="property-footer">
                        <strong>From {formatPrice(property.price)}</strong>
                        <Link to={url}>Enquire <i className="fa-solid fa-arrow-right" aria-hidden="true" /></Link>
                      </div>
                      <Link className="property-viewing-link" to={url}>Request a Viewing</Link>
                    </div>
                  </article>
                );
              })}
              {loading && [1, 2, 3].map((item) => (
                <PropertyCardSkeleton key={`loading-more-${item}`} label="Loading more properties" />
              ))}
            </div>
          )}

          {!error && properties.length > 0 && page < totalPages && (
            <div className="property-load-more">
              <button type="button" disabled={loading} onClick={() => setPage((current) => current + 1)}>
                {loading ? 'Loading...' : 'Load more properties'}
              </button>
              <span>Showing {properties.length} of {totalRecords.toLocaleString('en-US')}</span>
            </div>
          )}
        </section>
      </main>
      <Footer />
      <a className="advisor-button is-visible" href="https://api.whatsapp.com/send?phone=+971507135432&amp;text=Hi" target="_blank" rel="noopener noreferrer" aria-label="Speak with an advisor on WhatsApp">
        <svg className="advisor-button__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M21 11.5a8.5 8.5 0 0 1-12.7 7.4L3 20l1.2-5A8.5 8.5 0 1 1 21 11.5Z" />
        </svg>
        <span>Speak with an advisor</span>
      </a>
    </>
  );
};

export default Properties;
